// src/pages/Stats.tsx
import React from "react";
import AppShell from "../layout/AppShell";
import LoadingSpinner from "../components/LoadingSpinner";
import {
  getStats,
  getCertsStats,
  type Range,
  type DailyBucket,
  type Kpis,
  type CertsKpis,
} from "../services/statsService";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  BarChart,
  Bar,
  CartesianGrid,
} from "recharts";
import { MdInsights, MdWorkspacePremium } from "react-icons/md";

/* ───────────────────── helpers UI ───────────────────── */
const COLORS = {
  primary: "var(--color-primary)",
  secondary:
    "color-mix(in oklab, var(--color-primary) 40%, var(--color-onSurface) 10%)",
  surface: "var(--color-surface)",
  onSurface: "var(--color-onSurface)",
  green: "oklch(0.72 0.12 155)",
  orange: "oklch(0.74 0.13 60)",
  blue: "oklch(0.7 0.12 240)",
  violet: "oklch(0.67 0.14 300)",
};

const DOW_LABELS = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"] as const;
const DOW_INDEXES = [0, 1, 2, 3, 4, 5, 6] as const;
type DowIndex = (typeof DOW_INDEXES)[number];

/* ───────────────────── tipos locales ───────────────────── */
type TrendRow = DailyBucket & { ma7: number };
type WeekdayRow = {
  dow: (typeof DOW_LABELS)[number];
  posts: number;
  comments: number;
};

/* ───────────────────── componente ───────────────────── */
export default function Stats(): React.ReactElement {
  // rango común (últimos 30 por default)
  const [range, setRange] = React.useState<Range>({
    kind: "lastDays",
    days: 30,
  });

  // Comunidad
  const [kpis, setKpis] = React.useState<Kpis | null>(null);
  const [daily, setDaily] = React.useState<DailyBucket[]>([]);
  const [loadingCommunity, setLoadingCommunity] = React.useState(true);

  // Certificaciones
  const [certs, setCerts] = React.useState<CertsKpis | null>(null);
  const [loadingCerts, setLoadingCerts] = React.useState(true);

  React.useEffect(() => {
    let alive = true;

    (async () => {
      try {
        setLoadingCommunity(true);
        const { kpis, daily } = await getStats(range);
        if (!alive) return;
        setKpis(kpis);
        setDaily(daily);
      } finally {
        if (alive) setLoadingCommunity(false);
      }
    })();

    (async () => {
      try {
        setLoadingCerts(true);
        const c = await getCertsStats(range);
        if (!alive) return;
        setCerts(c);
      } finally {
        if (alive) setLoadingCerts(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [range]);

  /* ───── derivados comunidad ───── */
  const trend: TrendRow[] = React.useMemo(() => {
    // MA7 sobre posts
    const out: TrendRow[] = [];
    const buf: number[] = [];
    let sum = 0;
    daily.forEach((d) => {
      buf.push(d.posts);
      sum += d.posts;
      if (buf.length > 7) sum -= buf.shift()!;
      const ma7 = buf.length > 0 ? Number((sum / buf.length).toFixed(2)) : 0;
      out.push({ ...d, ma7 });
    });
    return out;
  }, [daily]);

  const weekdayDist: WeekdayRow[] = React.useMemo(() => {
    // Aggregador estrictamente tipado a 0..6
    const agg: Record<DowIndex, { posts: number; comments: number }> = {
      0: { posts: 0, comments: 0 },
      1: { posts: 0, comments: 0 },
      2: { posts: 0, comments: 0 },
      3: { posts: 0, comments: 0 },
      4: { posts: 0, comments: 0 },
      5: { posts: 0, comments: 0 },
      6: { posts: 0, comments: 0 },
    };

    for (const d of daily) {
      // Evita “possibly undefined” y la conversión string[] -> number[]
      const [yStr, mStr, dayStr] = d.date.split("-");
      const y = Number(yStr) || 0;
      const m = Number(mStr) || 1; // 1..12
      const dd = Number(dayStr) || 1; // 1..31

      const dow = new Date(y, m - 1, dd).getDay() as DowIndex;
      agg[dow].posts += d.posts;
      agg[dow].comments += d.comments;
    }

    // Map con índices 0..6 para garantizar que nunca hay `undefined`
    return DOW_INDEXES.map((i) => ({
      dow: DOW_LABELS[i],
      posts: agg[i].posts,
      comments: agg[i].comments,
    }));
  }, [daily]);

  const engagement = React.useMemo(() => {
    if (!kpis || (kpis.postsTotal ?? 0) === 0) return 0;
    const inter =
      (kpis.likesTotal ?? 0) +
      (kpis.recommendsTotal ?? 0) +
      (kpis.commentsTotal ?? 0);
    return Number((inter / (kpis.postsTotal || 1)).toFixed(2));
  }, [kpis]);

  const bestDay = React.useMemo(() => {
    if (daily.length === 0) return "—";
    const max = daily.reduce((a, b) => (b.posts > a.posts ? b : a));
    return `${max.date} · ${max.posts}`;
  }, [daily]);

  /* ───── datos pie de certificaciones ───── */
  const pieTypes = React.useMemo(
    () =>
      certs
        ? [
            { name: "Oficiales", value: certs.official },
            { name: "De prueba", value: certs.trial },
          ]
        : [],
    [certs]
  );
  const pieStatus = React.useMemo(
    () =>
      certs
        ? [
            { name: "Draft", value: certs.draft },
            { name: "Final", value: certs.final },
          ]
        : [],
    [certs]
  );

  const convRate = React.useMemo(() => {
    if (!certs) return 0;
    const denom = certs.final + certs.draft;
    return denom > 0 ? Number(((certs.final / denom) * 100).toFixed(1)) : 0;
  }, [certs]);

  return (
    <AppShell>
      <section className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* ───────── Comunidad ───────── */}
        <div className="card p-4">
          <header className="mb-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="icon-accent text-2xl">
                <MdInsights />
              </span>
              <h2 className="text-lg font-semibold">
                Estadísticas — Comunidad
              </h2>
            </div>

            <select
              className="input-surface w-[160px]"
              value={range.kind === "lastDays" ? String(range.days) : "30"}
              onChange={(e) =>
                setRange({ kind: "lastDays", days: Number(e.target.value) })
              }
              title="Rango"
            >
              <option value="7">7 días</option>
              <option value="14">14 días</option>
              <option value="30">30 días</option>
              <option value="90">90 días</option>
            </select>
          </header>

          {/* KPIs */}
          <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-4">
            <Kpi
              label="Posts"
              value={kpis?.postsTotal}
              loading={loadingCommunity}
            />
            <Kpi
              label="Likes"
              value={kpis?.likesTotal}
              loading={loadingCommunity}
            />
            <Kpi
              label="Recomendaciones"
              value={kpis?.recommendsTotal}
              loading={loadingCommunity}
            />
            <Kpi
              label="Comentarios"
              value={kpis?.commentsTotal}
              loading={loadingCommunity}
            />
          </div>

          {/* Área apilada + MA7 */}
          <div className="h-[280px] w-full rounded-xl border border-surface bg-surface/40 p-2">
            {loadingCommunity ? (
              <div className="flex h-full items-center justify-center">
                <LoadingSpinner />
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trend}>
                  <CartesianGrid strokeOpacity={0.1} />
                  <XAxis dataKey="date" tick={{ fill: COLORS.onSurface }} />
                  <YAxis tick={{ fill: COLORS.onSurface }} />
                  <Tooltip />
                  <Legend />
                  <Area
                    type="monotone"
                    dataKey="posts"
                    name="Posts"
                    stroke={COLORS.blue}
                    fill={COLORS.blue}
                    fillOpacity={0.25}
                  />
                  <Area
                    type="monotone"
                    dataKey="likes"
                    name="Likes"
                    stroke={COLORS.green}
                    fill={COLORS.green}
                    fillOpacity={0.25}
                  />
                  <Area
                    type="monotone"
                    dataKey="recommends"
                    name="Recom."
                    stroke={COLORS.violet}
                    fill={COLORS.violet}
                    fillOpacity={0.25}
                  />
                  <Area
                    type="monotone"
                    dataKey="comments"
                    name="Comentarios"
                    stroke={COLORS.orange}
                    fill={COLORS.orange}
                    fillOpacity={0.25}
                  />
                  <Line
                    type="monotone"
                    dataKey="ma7"
                    name="MA7 (posts)"
                    stroke={COLORS.primary}
                    strokeWidth={2}
                    dot={false}
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Derivados / mini KPIs */}
          <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-3">
            <MiniKpi
              label="Engagement promedio / post"
              value={engagement}
              suffix=""
              formatter={(n) => `${n.toFixed(2)}`}
            />
            <MiniKpi
              label="Día más activo (posts)"
              value={null}
              raw={`${bestDay}`}
            />
            <MiniKpi
              label="Días con actividad"
              value={daily.filter((d) => d.posts > 0).length}
              suffix=""
            />
          </div>

          {/* Distribución por día de la semana */}
          <div className="mt-4 h-[220px] w-full rounded-xl border border-surface bg-surface/40 p-2">
            {loadingCommunity ? (
              <div className="flex h-full items-center justify-center">
                <LoadingSpinner />
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={weekdayDist}>
                  <CartesianGrid strokeOpacity={0.1} />
                  <XAxis dataKey="dow" tick={{ fill: COLORS.onSurface }} />
                  <YAxis tick={{ fill: COLORS.onSurface }} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="posts" name="Posts" fill={COLORS.blue} />
                  <Bar
                    dataKey="comments"
                    name="Comentarios"
                    fill={COLORS.orange}
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* ───────── Certificaciones ───────── */}
        <div className="card p-4">
          <header className="mb-3 flex items-center gap-2">
            <span className="icon-accent text-2xl">
              <MdWorkspacePremium />
            </span>
            <h2 className="text-lg font-semibold">
              Estadísticas — Certificaciones
            </h2>
          </header>

          {/* KPIs simples */}
          <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-4">
            <Kpi label="Totales" value={certs?.total} loading={loadingCerts} />
            <Kpi
              label="Oficiales"
              value={certs?.official}
              loading={loadingCerts}
            />
            <Kpi
              label="De prueba"
              value={certs?.trial}
              loading={loadingCerts}
            />
            <Kpi
              label="Finalizadas"
              value={certs?.final}
              loading={loadingCerts}
            />
          </div>

          {/* Conversión y split como barras de progreso */}
          <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-2">
            <ProgressBlock
              title="Conversión (Final vs Draft)"
              loading={loadingCerts}
              items={[
                {
                  label: "Final",
                  value: certs?.final ?? 0,
                  color: COLORS.green,
                },
                {
                  label: "Draft",
                  value: certs?.draft ?? 0,
                  color: COLORS.orange,
                },
              ]}
              total={(certs?.final ?? 0) + (certs?.draft ?? 0)}
              extraRight={certs ? `${convRate}% finalizadas` : "—"}
            />
            <ProgressBlock
              title="Split por tipo"
              loading={loadingCerts}
              items={[
                {
                  label: "Oficial",
                  value: certs?.official ?? 0,
                  color: COLORS.primary,
                },
                {
                  label: "Prueba",
                  value: certs?.trial ?? 0,
                  color: COLORS.violet,
                },
              ]}
              total={certs?.total ?? 0}
            />
          </div>

          {/* Dos pies side-by-side */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="rounded-xl border border-surface bg-surface/40 p-2">
              <h3 className="mb-2 text-sm font-semibold">Por tipo</h3>
              <div className="h-[220px] w-full">
                {loadingCerts ? (
                  <div className="flex h-full items-center justify-center">
                    <LoadingSpinner />
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={pieTypes}
                        dataKey="value"
                        nameKey="name"
                        innerRadius={50}
                        outerRadius={80}
                        paddingAngle={2}
                      >
                        <Cell fill={COLORS.primary} />
                        <Cell fill={COLORS.violet} />
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            <div className="rounded-xl border border-surface bg-surface/40 p-2">
              <h3 className="mb-2 text-sm font-semibold">Por estado</h3>
              <div className="h-[220px] w-full">
                {loadingCerts ? (
                  <div className="flex h-full items-center justify-center">
                    <LoadingSpinner />
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={pieStatus}
                        dataKey="value"
                        nameKey="name"
                        innerRadius={50}
                        outerRadius={80}
                        paddingAngle={2}
                      >
                        <Cell fill={COLORS.orange} />
                        <Cell fill={COLORS.green} />
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </AppShell>
  );
}

/* ───────────────────── subcomponentes ───────────────────── */
function Kpi({
  label,
  value,
  loading,
}: {
  label: string;
  value: number | null | undefined;
  loading?: boolean;
}) {
  return (
    <div className="rounded-xl border border-surface bg-surface/60 p-3">
      <div className="text-xs opacity-70">{label}</div>
      <div className="mt-1 text-2xl font-semibold tabular-nums">
        {loading ? "—" : (value ?? 0).toLocaleString()}
      </div>
    </div>
  );
}

function MiniKpi({
  label,
  value,
  suffix = "",
  raw,
  formatter,
}: {
  label: string;
  value: number | null;
  suffix?: string;
  raw?: string;
  formatter?: (n: number) => string;
}) {
  return (
    <div className="rounded-xl border border-surface bg-surface/60 p-3">
      <div className="text-xs opacity-70">{label}</div>
      <div className="mt-1 text-xl font-semibold tabular-nums">
        {raw
          ? raw
          : value === null
          ? "—"
          : formatter
          ? `${formatter(value)}${suffix}`
          : `${value.toLocaleString()}${suffix}`}
      </div>
    </div>
  );
}

function ProgressBlock({
  title,
  items,
  total,
  extraRight,
  loading,
}: {
  title: string;
  items: Array<{ label: string; value: number; color: string }>;
  total: number;
  extraRight?: string;
  loading?: boolean;
}) {
  return (
    <div className="rounded-xl border border-surface bg-surface/40 p-3">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-sm font-semibold">{title}</h3>
        <div className="text-xs opacity-70">
          {loading
            ? "—"
            : extraRight ?? `Total: ${(total || 0).toLocaleString()}`}
        </div>
      </div>
      <div className="space-y-3">
        {items.map((it) => {
          const pct = total > 0 ? Math.round((it.value / total) * 100) : 0;
          return (
            <div key={it.label}>
              <div className="mb-1 flex items-center justify-between text-sm">
                <span className="opacity-80">{it.label}</span>
                <span className="tabular-nums">
                  {loading ? "—" : `${it.value} · ${pct}%`}
                </span>
              </div>
              <div className="h-2 w-full overflow-hidden rounded-full bg-surface/60">
                <div
                  className="h-2 rounded-full"
                  style={{ width: `${pct}%`, background: it.color }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
